/**
 * HTML Domains Control Panel component.
 * @module components/manage/Controlpanels/HTMLDomainsControlpanel
 */

import React, { Component } from 'react';
import {
  getControlpanel,
  updateControlpanel,
} from '@plone/volto/actions/controlpanels/controlpanels';
import { Form } from '@plone/volto/components/manage/Form';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import {
  extractInvariantErrors,
  tryParseJSON,
} from '@plone/volto/helpers/FormValidation/FormValidation';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import config from '@plone/volto/registry';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import { compose } from 'redux';
import {
  Button,
  Container,
  Loader,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';

import clearSVG from '@plone/volto/icons/clear.svg';
import saveSVG from '@plone/volto/icons/save.svg';

const messages = defineMessages({
  changesSaved: {
    id: 'Changes saved.',
    defaultMessage: 'Changes saved.',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  save: {
    id: 'Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  info: {
    id: 'Info',
    defaultMessage: 'Info',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  auditHistory: {
    id: 'Audit History',
    defaultMessage: 'Histórico de Alterações',
  },
  timestamp: {
    id: 'Timestamp',
    defaultMessage: 'Data/Hora',
  },
  username: {
    id: 'Username',
    defaultMessage: 'Usuário',
  },
  action: {
    id: 'Action',
    defaultMessage: 'Ação',
  },
  domain: {
    id: 'Domain',
    defaultMessage: 'Domínio',
  },
  fieldName: {
    id: 'Field Name',
    defaultMessage: 'Campo',
  },
  added: {
    id: 'Added',
    defaultMessage: 'Adicionado',
  },
  removed: {
    id: 'Removed',
    defaultMessage: 'Removido',
  },
  domainsWithConsent: {
    id: 'Domains requiring consent',
    defaultMessage: 'Domínios que requerem consentimento',
  },
  domainsWithoutConsent: {
    id: 'Domains not requiring consent',
    defaultMessage: 'Domínios que não requerem consentimento',
  },
});

/**
 * HTMLDomainsControlpanel class.
 * @class HTMLDomainsControlpanel
 * @extends Component
 */
class HTMLDomainsControlpanel extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    updateControlpanel: PropTypes.func.isRequired,
    getControlpanel: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    updateRequest: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    controlpanel: PropTypes.shape({
      '@id': PropTypes.string,
      data: PropTypes.object,
      schema: PropTypes.object,
      title: PropTypes.string,
    }),
    pathname: PropTypes.string.isRequired,
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    controlpanel: null,
  };

  /**
   * Constructor
   * @method constructor
   * @param {Object} props Component properties
   * @constructs HTMLDomainsControlpanel
   */
  constructor(props) {
    super(props);
    this.onCancel = this.onCancel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.form = React.createRef();
    this.state = {
      isClient: false,
      error: null,
      auditData: null,
      loadingAudit: true,
    };
  }

  /**
   * Component did mount
   * @method componentDidMount
   * @returns {undefined}
   */
  componentDidMount() {
    this.props.getControlpanel(this.props.id);
    this.fetchAuditHistory();
    this.setState({ isClient: true });
  }

  /**
   * Fetch audit history
   * @method fetchAuditHistory
   * @returns {undefined}
   */
  fetchAuditHistory = async () => {
    const { settings } = config;
    const apiPath = settings.apiPath || '';
    // Add timestamp to prevent backend caching
    const cacheBuster = `_=${Date.now()}`;

    try {
      const response = await fetch(
        `${apiPath}/++api++/@html-domains-audit?${cacheBuster}`,
        {
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
          credentials: 'include',
        },
      );

      if (response.ok) {
        const data = await response.json();
        this.setState({
          auditData: data,
          loadingAudit: false,
        });
      } else {
        this.setState({
          auditData: null,
          loadingAudit: false,
        });
      }
    } catch (error) {
      // Error fetching audit history - silently fail
      this.setState({
        auditData: null,
        loadingAudit: false,
      });
    }
  };

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.updateRequest.loading && nextProps.updateRequest.loaded) {
      toast.success(
        <Toast
          success
          title={this.props.intl.formatMessage(messages.info)}
          content={this.props.intl.formatMessage(messages.changesSaved)}
        />,
      );
      // Refresh audit history after save
      this.fetchAuditHistory();
    }
    if (this.props.updateRequest.loading && nextProps.updateRequest.error) {
      const message =
        nextProps.updateRequest.error?.response?.body?.error?.message ||
        nextProps.updateRequest.error?.response?.body?.message ||
        nextProps.updateRequest.error?.response?.text ||
        '';

      const error =
        new DOMParser().parseFromString(message, 'text/html')?.all?.[0]
          ?.textContent || message;

      const errorsList = tryParseJSON(error);
      let invariantErrors = [];
      if (Array.isArray(errorsList)) {
        invariantErrors = extractInvariantErrors(errorsList);
      }

      this.setState({ error: error });

      if (invariantErrors.length > 0) {
        toast.error(
          <Toast
            error
            title={this.props.intl.formatMessage(messages.error)}
            content={invariantErrors.join(' - ')}
          />,
        );
      } else if (error) {
        toast.error(
          <Toast
            error
            title={this.props.intl.formatMessage(messages.error)}
            content={error}
          />,
        );
      }
    }
  }

  /**
   * Submit handler
   * @method onSubmit
   * @param {object} data Form data.
   * @returns {undefined}
   */
  onSubmit = (data) => {
    this.props.updateControlpanel(`/@controlpanels/${this.props.id}`, data);
  };

  /**
   * Cancel handler
   * @method onCancel
   * @returns {undefined}
   */
  onCancel = () => {
    this.props.history.goBack();
  };

  /**
   * Format timestamp
   * @method formatTimestamp
   * @param {string} timestamp ISO timestamp
   * @returns {string} Formatted date
   */
  formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (error) {
      return timestamp;
    }
  };

  /**
   * Format field name
   * @method formatFieldName
   * @param {string} fieldName Field name
   * @returns {string} Formatted field name
   */
  formatFieldName = (fieldName) => {
    if (fieldName === 'domains_with_consent') {
      return this.props.intl.formatMessage(messages.domainsWithConsent);
    } else if (fieldName === 'domains_without_consent') {
      return this.props.intl.formatMessage(messages.domainsWithoutConsent);
    }
    return fieldName;
  };

  /**
   * Format action
   * @method formatAction
   * @param {string} action Action type
   * @returns {string} Formatted action
   */
  formatAction = (action) => {
    if (action === 'added') {
      return this.props.intl.formatMessage(messages.added);
    } else if (action === 'removed') {
      return this.props.intl.formatMessage(messages.removed);
    }
    return action;
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const { filterControlPanelsSchema } = config.settings;
    const { auditData, loadingAudit } = this.state;

    if (this.props.controlpanel) {
      return (
        <div id="page-controlpanel">
          <Helmet title={this.props.controlpanel.title} />
          <Container>
            <Form
              ref={this.form}
              title={this.props.controlpanel.title}
              schema={filterControlPanelsSchema(this.props.controlpanel)}
              formData={this.props.controlpanel.data}
              requestError={this.state.error}
              onSubmit={this.onSubmit}
              onCancel={this.onCancel}
              hideActions
              loading={this.props.updateRequest.loading}
            />

            {/* Audit History Section */}
            <Segment style={{ marginTop: '2rem' }}>
              <h2>{this.props.intl.formatMessage(messages.auditHistory)}</h2>
              {loadingAudit ? (
                <Loader active inline="centered" />
              ) : auditData && auditData.items && auditData.items.length > 0 ? (
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        {this.props.intl.formatMessage(messages.timestamp)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {this.props.intl.formatMessage(messages.username)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {this.props.intl.formatMessage(messages.action)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {this.props.intl.formatMessage(messages.domain)}
                      </Table.HeaderCell>
                      <Table.HeaderCell>
                        {this.props.intl.formatMessage(messages.fieldName)}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {auditData.items.map((item) => (
                      <Table.Row key={item.id}>
                        <Table.Cell>
                          {this.formatTimestamp(item.timestamp)}
                        </Table.Cell>
                        <Table.Cell>{item.username}</Table.Cell>
                        <Table.Cell>
                          {this.formatAction(item.action)}
                        </Table.Cell>
                        <Table.Cell
                          style={{
                            wordBreak: 'break-all',
                            maxWidth: '300px',
                          }}
                        >
                          {item.domain}
                        </Table.Cell>
                        <Table.Cell>
                          {this.formatFieldName(item.field_name)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              ) : (
                <Message info>
                  <FormattedMessage
                    id="No audit history available"
                    defaultMessage="Nenhum histórico de alterações disponível."
                  />
                </Message>
              )}
            </Segment>
          </Container>
          {this.state.isClient &&
            createPortal(
              <Toolbar
                pathname={this.props.pathname}
                hideDefaultViewButtons
                inner={
                  <>
                    <Button
                      id="toolbar-save"
                      className="save"
                      aria-label={this.props.intl.formatMessage(messages.save)}
                      onClick={() => this.form.current.onSubmit()}
                      disabled={this.props.updateRequest.loading}
                      loading={this.props.updateRequest.loading}
                    >
                      <Icon
                        name={saveSVG}
                        className="circled"
                        size="30px"
                        title={this.props.intl.formatMessage(messages.save)}
                      />
                    </Button>
                    <Button
                      className="cancel"
                      aria-label={this.props.intl.formatMessage(
                        messages.cancel,
                      )}
                      onClick={() => this.onCancel()}
                    >
                      <Icon
                        name={clearSVG}
                        className="circled"
                        size="30px"
                        title={this.props.intl.formatMessage(messages.cancel)}
                      />
                    </Button>
                  </>
                }
              />,
              document.getElementById('toolbar'),
            )}
        </div>
      );
    }
    return <div />;
  }
}

export default compose(
  withRouter,
  injectIntl,
  connect(
    (state, props) => ({
      controlpanel: state.controlpanels.controlpanel,
      updateRequest: state.controlpanels.update,
    }),
    { updateControlpanel, getControlpanel },
  ),
)(HTMLDomainsControlpanel);
